/** TITLEFLOW-ORDER-ENTRY-DOCUMENTS-001 — compact optional "Prior Search Package" picker for the Create Order popup. */
import React, { useRef } from 'react';
import { Paperclip, X, FileText } from 'lucide-react';
import { toast } from 'sonner';

export const PRIOR_SEARCH_PACKAGE_CATEGORY = 'prior_search_package';
export const PRIOR_SEARCH_ACCEPT = '.pdf,.doc,.docx';
const MAX_FILES = 10;
const MAX_SIZE = 15 * 1024 * 1024;
const OK_EXT = ['.pdf', '.doc', '.docx'];

const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export const PriorSearchPackagePicker = ({ files, onChange, disabled }) => {
  const inputRef = useRef(null);

  const addFiles = (list) => {
    const next = [...files];
    const rejected = [];
    for (const f of Array.from(list || [])) {
      const ext = f.name.includes('.') ? `.${f.name.split('.').pop().toLowerCase()}` : '';
      if (!OK_EXT.includes(ext)) { rejected.push(`${f.name} — only PDF or Word`); continue; }
      if (f.size > MAX_SIZE) { rejected.push(`${f.name} — over 15 MB`); continue; }
      if (next.some(x => x.name === f.name && x.size === f.size)) continue;
      if (next.length >= MAX_FILES) { rejected.push(`${f.name} — maximum ${MAX_FILES} files`); continue; }
      next.push(f);
    }
    if (rejected.length) toast.error('Some files were not added', { description: rejected.slice(0, 3).join(' · ') });
    onChange(next);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="relative overflow-hidden rounded-2xl border border-indigo-100 bg-gradient-to-r from-blue-50/70 via-white to-violet-50/80 px-3.5 py-2.5 shadow-[0_1px_3px_rgba(15,23,42,0.05)]" data-testid="prior-search-package">
      <div aria-hidden className="pointer-events-none absolute -right-6 -top-8 h-24 w-24 rounded-full bg-violet-200/40 blur-2xl" />
      <div className="relative flex items-center gap-3 min-w-0">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-indigo-600 shadow-[0_1px_2px_rgba(15,23,42,0.06)] ring-1 ring-indigo-100">
          <Paperclip className="h-4 w-4" />
        </span>
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
          <p className="truncate text-[13px] font-semibold leading-5 text-slate-800">Attach Prior Search Package</p>
          <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-medium text-slate-500 ring-1 ring-slate-200">Optional</span>
          <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-700 ring-1 ring-indigo-100">Internal only</span>
        </div>
        {files.length > 0 && <span className="shrink-0 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 px-2 py-0.5 text-[11px] font-semibold text-white" data-testid="prior-search-package-count">{files.length}</span>}
        <button type="button" disabled={disabled} onClick={() => inputRef.current?.click()} data-testid="prior-search-package-add"
          className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-indigo-200 bg-white px-3 text-xs font-semibold text-indigo-700 shadow-sm transition-colors hover:border-indigo-300 hover:bg-indigo-50 disabled:opacity-50">
          <Paperclip className="h-3.5 w-3.5" />{files.length ? 'Add more' : 'Attach'}
        </button>
      </div>
      <input ref={inputRef} type="file" multiple accept={PRIOR_SEARCH_ACCEPT} className="hidden" onChange={(e) => addFiles(e.target.files)} data-testid="prior-search-package-input" />
      {files.length > 0 && (
        <div className="relative mt-2 flex flex-wrap gap-1.5" data-testid="prior-search-package-chips">
          {files.map((f, i) => (
            <span key={`${f.name}-${f.size}`} className="inline-flex max-w-[240px] items-center gap-1.5 rounded-lg border border-indigo-100 bg-white px-2 py-1 text-[11px] text-slate-700 shadow-[0_1px_2px_rgba(15,23,42,0.04)]" data-testid={`prior-search-package-chip-${i}`}>
              <FileText className="h-3 w-3 shrink-0 text-indigo-600" />
              <span className="truncate font-medium">{f.name}</span>
              <span className="text-slate-400">{fmtSize(f.size)}</span>
              <button type="button" disabled={disabled} aria-label={`Remove ${f.name}`} onClick={() => onChange(files.filter((_, j) => j !== i))} className="ml-0.5 rounded p-0.5 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600" data-testid={`prior-search-package-remove-${i}`}>
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/** Order-first upload: runs only after the order exists. Returns { uploaded: [names], failed: [{name, reason}] }. */
export const uploadPriorSearchPackage = async (axios, apiBase, orderId, files) => {
  const uploaded = [];
  const failed = [];
  for (const f of files) {
    const fd = new FormData();
    fd.append('file', f);
    fd.append('category', PRIOR_SEARCH_PACKAGE_CATEGORY);
    try {
      await axios.post(`${apiBase}/orders/${orderId}/files/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      uploaded.push(f.name);
    } catch (e) {
      const d = e?.response?.data?.detail;
      failed.push({ name: f.name, reason: typeof d === 'string' ? d : d?.message || e.message || 'Upload failed' });
    }
  }
  return { uploaded, failed };
};
